import type { TranslateProps } from '@tolgee/react';
import { tolgee } from './tolgee';

type tTranslateParamValue = string | number | bigint | Date;

/**
 * The i18n-js-style options bag accepted by `t()` and `<Translate params>`:
 * interpolation values plus the legacy `scope` / `defaultValue` / `locale`
 * options, all in one object.
 */
export interface ITranslateOptions {
  scope?: string;
  defaultValue?: string;
  /** Legacy i18n-js per-call locale — mapped to Tolgee's `language` option. */
  locale?: string;
  count?: number;
  [param: string]: unknown;
}

const toTolgeeParams = (
  raw: Record<string, unknown>
): Record<string, tTranslateParamValue> => {
  const params: Record<string, tTranslateParamValue> = {};
  Object.entries(raw).forEach(([name, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    params[name] =
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'bigint' ||
      value instanceof Date
        ? value
        : String(value);
  });
  return params;
};

/**
 * Maps the i18n-js call convention onto Tolgee's props-object form:
 * `scope` becomes a key prefix, `defaultValue` is lifted out of the params
 * (Tolgee treats unknown option keys as interpolation params), and `locale`
 * becomes a per-call `language` override.
 */
export const buildTranslateProps = (
  key: string,
  options?: ITranslateOptions
): TranslateProps<tTranslateParamValue> => {
  const { scope, defaultValue, locale, ...params } = options ?? {};
  return {
    // @tolgee-ignore
    key: scope ? `${scope}.${key}` : key,
    defaultValue,
    params: toTolgeeParams(params),
    ...(locale ? { language: locale } : {})
  };
};

/**
 * Imperative translation helper, usable outside React components. Reads from
 * the Tolgee cache synchronously (en/nb are bundled; other languages resolve
 * once loaded). Not reactive — components should render via `<Translate>`.
 */
export const t = (key: string, options?: ITranslateOptions): string =>
  tolgee.t(buildTranslateProps(key, options));
